import { hasStorageAvailable } from '@klv/shared/utils/storage';
import type { ICalculateRoiType, INewSubscriberRevenueType, ICapaignsRevenueType, IAutomatedFlowsRevenueType } from '../types';
import pricingData from '@klv/shared/pricing/static/us.json';

type TSmsPlans = keyof typeof pricingData['sms'];

const revenueFromNewSubscribersEmail = ({ webVisitors, ordersPerMonth, averageOrderSize }: INewSubscriberRevenueType) => {
  const submitRate = .0226;
  return submitRate * webVisitors * ordersPerMonth * averageOrderSize * 12;
};

const revenueFromCampaignsEmail = ({ activeProfiles, ordersPerMonth, averageOrderSize }: ICapaignsRevenueType) => {
  const placedOrderRate = .0009;
  return placedOrderRate * activeProfiles * ordersPerMonth * averageOrderSize * 12;
};

const revenueFromAutomatedFlowsEmail = ({ flowsPerMonth, averageOrderSize }: IAutomatedFlowsRevenueType) => {
  const placedOrderRateOnFlows = .0171;
  return placedOrderRateOnFlows * flowsPerMonth * averageOrderSize * 12;
};

export const calculateRoiEmail = ({ activeProfiles, ordersPerMonth, averageOrderSize, webVisitors, flowsPerMonth }: ICalculateRoiType) => {
  const subscriberRevenue = revenueFromNewSubscribersEmail({ webVisitors, ordersPerMonth, averageOrderSize });
  const campaignsRevenue = revenueFromCampaignsEmail({ activeProfiles, ordersPerMonth, averageOrderSize });
  const flowsRevenue = revenueFromAutomatedFlowsEmail({ flowsPerMonth, averageOrderSize });
  const foundPrice = Object.values(pricingData.email).find(val => activeProfiles >= val.min_customers && activeProfiles <= val.max_customers);
  const klaviyoCost = (foundPrice?.price || 0) * 12;
  const total = Math.round(((subscriberRevenue + campaignsRevenue + flowsRevenue - klaviyoCost) / klaviyoCost) || 0);
  return {
    total :  Math.round(total).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    subscriberRevenue:  '$' + Math.round(subscriberRevenue).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    campaignsRevenue:  '$' + Math.round(campaignsRevenue).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    flowsRevenue: '$' + Math.round(flowsRevenue).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    activeProfiles,
  };
};

const revenueFromNewSubscribersEmailAndSms = ({ webVisitors, ordersPerMonth, averageOrderSize }: INewSubscriberRevenueType) => {
  const submitRate = .0301;
  return submitRate * webVisitors * ordersPerMonth * averageOrderSize * 12;
};

const revenueFromCampaignsEmailAndSms = ({ activeProfiles, ordersPerMonth, averageOrderSize }: ICapaignsRevenueType) => {
  const placedOrderRate = .0010;
  return placedOrderRate * activeProfiles * ordersPerMonth * averageOrderSize * 12;
};

const revenueFromAutomatedFlowsEmailAndSms = ({ flowsPerMonth, averageOrderSize }: IAutomatedFlowsRevenueType) => {
  const placedOrderRateOnFlows = .0171;
  return placedOrderRateOnFlows * flowsPerMonth * averageOrderSize * 12;
};

export const calculateRoiEmailAndSms = ({ activeProfiles, ordersPerMonth, averageOrderSize, webVisitors, flowsPerMonth, smsPerMonth }: ICalculateRoiType) => {
  const subscriberRevenue = revenueFromNewSubscribersEmailAndSms({ webVisitors, ordersPerMonth, averageOrderSize });
  const campaignsRevenue = revenueFromCampaignsEmailAndSms({ activeProfiles, ordersPerMonth, averageOrderSize });
  const flowsRevenue = revenueFromAutomatedFlowsEmailAndSms({ flowsPerMonth, averageOrderSize });
  const foundPrice = Object.values(pricingData.email).find(val => activeProfiles >= val.min_customers && activeProfiles <= val.max_customers);
  const smsBuckets = Object.keys(pricingData.sms);
  let foundSmsPriceBucket = '150';
  if ((smsPerMonth || 0) > 450000) {
    foundSmsPriceBucket = '450000';
  } else if ((smsPerMonth || 0) > 150 ) {
    foundSmsPriceBucket = smsBuckets.find((key, index) => (smsPerMonth || 0) >= Number(smsBuckets[index - 1]) && (smsPerMonth || 100000) <= Number(key)) || '150';
  } 
  const foundSmsPrice = pricingData?.sms[foundSmsPriceBucket as TSmsPlans]?.price;
  const klaviyoCost = ((foundPrice?.price || 0) + (foundSmsPrice || 0)) * 12;
  const total = Math.round(((subscriberRevenue + campaignsRevenue + flowsRevenue - klaviyoCost) / klaviyoCost) || 0);
  return {
    total :  Math.round(total).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    subscriberRevenue:  '$' + Math.round(subscriberRevenue).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    campaignsRevenue:  '$' + Math.round(campaignsRevenue).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    flowsRevenue: '$' + Math.round(flowsRevenue).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    activeProfiles,
  };
};

export const getInitialRoiFormValues = () => {
  if (hasStorageAvailable('localStorage') && localStorage.getItem('roiFormValues')) {
    const roiFormValues = JSON.parse(localStorage.getItem('roiFormValues') || '{}');
    const valuesToCalculate = {
      activeProfiles: Number(roiFormValues?.activeProfiles || 0),
      ordersPerMonth: Number(roiFormValues?.averageNumberOfOrders || 0),
      averageOrderSize: Number(roiFormValues?.averageOrderValue || 0),
      webVisitors: Number(roiFormValues?.webVisitors || 0),
      flowsPerMonth: Number(roiFormValues?.flowsPerMonth || 0),
    };
    const calculatedTotals = roiFormValues?.emailOnly ? calculateRoiEmail(valuesToCalculate) : calculateRoiEmailAndSms({
      ...valuesToCalculate,
      smsPerMonth: Number(roiFormValues?.smsPerMonth || 0),
    });
    const savedValues = {
      activeProfiles: roiFormValues.activeProfiles || '',
      webVisitors: roiFormValues.webVisitors || '',
      averageOrderValue: roiFormValues.averageOrderValue || '',
      averageNumberOfOrders: roiFormValues.averageNumberOfOrders || '',
      campaignsPerMonth: roiFormValues.campaignsPerMonth || '',
      flowsPerMonth: roiFormValues.flowsPerMonth || '',
      smsPerMonth: roiFormValues.smsPerMonth || '',
    };
    return { calculatedRoi: calculatedTotals, emailOnly: roiFormValues?.emailOnly, formVals: savedValues };
  }
  return false;
};
